import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getQuarter } from '@/helpers/date';
import { fetchPassthroughReport, fetchDropoffReport } from '@/api/insights';
import { format } from 'date-fns';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { isEmpty } from 'lodash-es';
import { getPassthroughRate } from '@/stores/insights/helpers';
import { assignInsightsTableToCSVDownloadLink } from '@/helpers/fileDownload';
import { numberWithCommas } from '@/helpers/formatters';
import { useDatasetCounts } from './datasetCounts';

export const usePassthroughDropoffReport = defineStore('passthroughDropoffReport', () => {
  const data = ref({
    total: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    active: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    net: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    dropped_out: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
    net_dropoff_rate: {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    },
  });
  const flags = ref({});
  const error = ref(null);

  const isEmptyData = computed(() => isEmpty(data.value)
    || Object.values(data.value)
      .every((item) => Object.values(item || {}).every((value) => value === 0)));

  const passthroughTableRows = computed(() => {
    const {
      total, active, net,
    } = data.value || {};

    return [
      {
        label: 'Total Applications',
        review: {
          value: total.review,
          percent: getPassthroughRate('review', 'screen', total),
        },
        screen: {
          value: total.screen,
          percent: getPassthroughRate('screen', 'assessment', total),
        },
        assessment: {
          value: total.assessment,
          percent: getPassthroughRate('assessment', 'offer', total),
        },
        offer: {
          value: total.offer,
          percent: getPassthroughRate('offer', 'hired', total),
        },
        hired: {
          value: total.hired,
        },
      },
      {
        label: 'Active Candidates',
        review: {
          value: active.review,
          percent: getPassthroughRate('review', 'screen', active),
        },
        screen: {
          value: active.screen,
          percent: getPassthroughRate('screen', 'assessment', active),
        },
        assessment: {
          value: active.assessment,
          percent: getPassthroughRate('assessment', 'offer', active),
        },
        offer: {
          value: active.offer,
          percent: getPassthroughRate('offer', 'hired', active),
        },
        hired: {
          value: active.hired,
        },
      },
      {
        label: 'Net Applications',
        review: {
          value: net.review,
          percent: getPassthroughRate('review', 'screen', net),
        },
        screen: {
          value: net.screen,
          percent: getPassthroughRate('screen', 'assessment', net),
        },
        assessment: {
          value: net.assessment,
          percent: getPassthroughRate('assessment', 'offer', net),
        },
        offer: {
          value: net.offer,
          percent: getPassthroughRate('offer', 'hired', net),
        },
        hired: {
          value: net.hired,
        },
      },
    ];
  });

  const dropoffTableTotals = computed(() => {
    const {
      total,
    } = data.value || {};

    return [
      {
        id: 'applied',
        value: total.review,
      },
      {
        id: 'screening',
        value: total.screen,
      },
      {
        id: 'assessment',
        value: total.assessment,
      },
      {
        id: 'offer',
        value: total.offer,
      },
      {
        id: 'hired',
        value: total.hired,
      },
    ];
  });

  const dropoffTableRows = computed(() => {
    const {
      dropped_out: droppedOut, net_dropoff_rate: netDropoffRate, net,
    } = data.value || {};

    return [
      {
        label: 'Dropped Out',
        review: {
          value: droppedOut?.review || 0,
        },
        screen: {
          value: droppedOut?.screen || 0,
        },
        assessment: {
          value: droppedOut?.assessment || 0,
        },
        offer: {
          value: droppedOut?.offer || 0,
        },
        hired: {
          value: droppedOut?.hired || 0,
        },
      },
      {
        label: 'Net Applications',
        review: {
          value: net?.review || 0,
        },
        screen: {
          value: net?.screen || 0,
        },
        assessment: {
          value: net?.assessment || 0,
        },
        offer: {
          value: net?.offer || 0,
        },
        hired: {
          value: net?.hired || 0,
        },
      },
      {
        label: 'Net Drop-Off Rate',
        rowClass: 'net-dropoff-rate',
        review: {
          value: `${Math.round((netDropoffRate?.review || 0) * 100)}%`,
        },
        screen: {
          value: `${Math.round((netDropoffRate?.screen || 0) * 100)}%`,
        },
        assessment: {
          value: `${Math.round((netDropoffRate?.assessment || 0) * 100)}%`,
        },
        offer: {
          value: `${Math.round((netDropoffRate?.offer || 0) * 100)}%`,
        },
        hired: {
          value: '',
        },
      },
    ];
  });

  const loadPassthroughReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    error.value = null;

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true,
    });

    try {
      const { data: responseData } = await fetchPassthroughReport({
        selectedDateRange: date, routeQuery, filters: selectedFilters, owned: selectedMyJobs,
      });

      data.value = responseData;

      if (responseData?.total?.review) {
        useDatasetCounts().setDatasetCounts(
          numberWithCommas(responseData?.total?.review || 0),
        );
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load passthrough report - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
      });
    }
  };

  const loadDropoffReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    error.value = null;

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true,
    });

    try {
      const { data: responseData, flags: responseFlags } = await fetchDropoffReport({
        selectedDateRange: date, routeQuery, filters: selectedFilters, owned: selectedMyJobs,
      });

      data.value = responseData;
      flags.value = responseFlags;

      if (responseData?.total?.review) {
        useDatasetCounts().setDatasetCounts(
          numberWithCommas(responseData?.total?.review || 0),
        );
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load dropoff report - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
      });
    }
  };

  const downloadPassthroughCSV = ({ dateRange, projectName }) => {
    assignInsightsTableToCSVDownloadLink({
      tableRows: passthroughTableRows.value,
      dateRange,
      projectName,
      page: 'pass-through-report',
    });
  };

  const downloadDropoffCSV = ({ dateRange, projectName }) => {
    assignInsightsTableToCSVDownloadLink({
      tableRows: dropoffTableRows.value,
      tableTotals: dropoffTableTotals.value,
      dateRange,
      projectName,
      page: 'dropoff-report',
    });
  };

  return {
    // State
    data,
    flags,
    error,
    passthroughTableRows,
    dropoffTableRows,
    dropoffTableTotals,

    // Computed
    isEmptyData,

    // Actions
    loadPassthroughReport,
    loadDropoffReport,
    downloadPassthroughCSV,
    downloadDropoffCSV,
  };
});
